import ExemploDeMetaCompartilhada from './ExemploDeMetaCompartilhada';
import { mascara, numero } from './ValorDoMes';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Goal } from '../types/Goal';
import { Transaction, TransactionType, PaymentType } from '../types/Transaction';
import { Category } from '../types/Category';
import {
  getGoals, createGoal, updateGoal, deleteGoal, createTransaction, getGoalTimeline, ligarCoach,
  GoalProjection, GoalTimeline,
} from '../services/api';
import './Goals.css';
import EntityQuickEntry from './EntityQuickEntry';
/* O MESMO número que o schema do servidor usa — ver utils/limites.ts. */
import { LIMITES } from '../utils/limites';
import SeletorDeMes, { rotuloDoMes } from './SeletorDeMes';
import MetaCompartilhadaCard from './MetaCompartilhadaCard';
import {
  ConvitesDeMeta, CriarMetaCompartilhada, useMetasCompartilhadas,
} from './MetasCompartilhadas';
import { formatPct, brlOrMasked } from '../utils/format';
import { valorExibido } from '../utils/monthView';
import { mensagemDeErro } from '../utils/mensagemDeErro';
import { useConfirmar } from '../contexts/Confirmacao';
import { abaInicialDeMetas } from '../utils/abaInicialDeMetas';
import FinnAvatar from './FinnAvatar';
import { EVENTO_METAS_MUDARAM } from './AdvisorChat';
import { useFechavelComVoltar } from '../contexts/VoltarDoCelular';

interface GoalsProps {
  /** O olhinho de "Ocultar valores" do cabeçalho. Ver `utils/format.ts`. */
  hideValues?: boolean;

  filteredTransactions: Transaction[];
  categories: Category[];
  activeMonthLabel: string;
  onGoalsChange?: () => void;
  /** Item 27: avisa o App para recarregar transações após um aporte. */
  onDepositRecorded?: () => void;
  refreshTrigger?: number;
}

type Tab = 'reserve' | 'category_limit';

const COLORS = ['#7c3aed', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#ec4899', '#14b8a6', '#f97316'];

const EMPTY_FORM = {
  type: 'reserve' as Tab,
  name: '',
  targetAmount: '',
  savedAmount: '',
  categoryId: '',
  color: COLORS[0],
  deadlineMonth: '',
};

/** O mês corrente em 'YYYY-MM', pelo relógio local. */
const mesCorrente = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/** Soma N meses a 'YYYY-MM'. */
const somarMeses = (mk: string, n: number): string => {
  const [y, m] = mk.split('-').map(Number);
  const d = new Date(y, m - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * Um teto vencido ainda aparece na lista, marcado.
 *
 * A regra de verdade é do servidor (`services/limiteVigente.ts`), que é quem
 * decide se um teto alerta e se a IA o conhece. Aqui é só EXIBIÇÃO — e é a
 * única leitura que quer o vencido: apagá-lo da tela faria um teto sumir
 * sozinho, e ela procuraria o que apagou.
 */
const limiteVencido = (g: { deadlineMonth?: string | null }): boolean =>
  !!g.deadlineMonth && g.deadlineMonth < mesCorrente();

/**
 * Os atalhos de prazo, que são como ela fala.
 *
 * O pedido foi "posso falar nesse mês ou nos próximos 6 meses" — ninguém pensa
 * um teto em "até janeiro de 2027", pensa em DURAÇÃO. O campo de mês continua
 * ali para quem quiser a data exata; os atalhos só escrevem nele.
 */
const ATALHOS_DE_PRAZO: { rotulo: string; meses: number | null }[] = [
  { rotulo: 'Sem prazo', meses: null },
  { rotulo: 'Só este mês', meses: 0 },
  { rotulo: '3 meses', meses: 2 },
  { rotulo: '6 meses', meses: 5 },
  { rotulo: '12 meses', meses: 11 },
];

/**
 * O que dizer quando a meta não tem data de conclusão.
 *
 * ── O relato ─────────────────────────────────────────────────────────────
 *
 * "não entendi pq no ritmo atual não chego, eu tenho 11 mil de saldo".
 *
 * A frase era uma só: "No ritmo atual você não chega — sua sobra mensal
 * projetada não é positiva." Ela estava CERTA e respondendo outra pergunta.
 *
 * Duas coisas estavam erradas nela:
 *
 * 1. **Vaga onde podia ser concreta.** "não é positiva" não diz quanto nem de
 *    quantos meses. Sem o número não dá para agir nem para discordar.
 *
 * 2. **Ignorava o caixa.** Uma meta de R$ 6 mil para quem tem R$ 11 mil em
 *    conta não é inalcançável — é uma transferência. Abrir com "você não
 *    chega" responde sobre o FLUXO com o tom de quem responde sobre a
 *    SITUAÇÃO, e some com a única ação que resolveria hoje.
 *
 * ── Por que o caixa não conta sozinho ────────────────────────────────────
 *
 * Metas só somam o que foi depositado nelas. Isso continua certo: dinheiro em
 * conta não está reservado para nada, e contá-lo automaticamente faria três
 * metas exibirem o mesmo dinheiro três vezes. É por isso que existem
 * `cashCoversThis` E `cashCoversAll` — com R$ 11 mil e metas de 6 e 20 mil,
 * cada meta isolada diria "você já tem", e juntas seriam R$ 26 mil.
 *
 * ── Por que a sobra negativa continua aparecendo ─────────────────────────
 *
 * Mesmo quando o caixa cobre. Esconder "seu fluxo mensal é negativo" porque
 * há reserva seria trocar um mal-entendido por um pior: a reserva acaba, e o
 * fluxo é o que decide quando.
 */
/**
 * A meta com PRAZO.
 *
 * ── Por que ela tem componente próprio ───────────────────────────────────
 *
 * Sem prazo, a pergunta é "com o que sobra, quando eu chego?" e a resposta é
 * uma DATA. Com prazo, a pergunta inverte — "para chegar quando eu quero,
 * quanto preciso separar?" — e a resposta é um VALOR MENSAL, que é o número
 * sobre o qual dá para agir.
 *
 * São duas perguntas diferentes com duas respostas de tipos diferentes;
 * enfiar as duas na mesma frase daria uma que não responde nenhuma bem.
 *
 * ── Os quatro estados, e por que nenhum some ─────────────────────────────
 *
 * `deadline-passed`  o prazo venceu e a meta não fechou. Não é "você não
 *                    chega": é um compromisso que já falhou, e projetar uma
 *                    data nova por cima esconderia isso.
 * no caminho         a sobra cobre o que o prazo pede.
 * falta X/mês        a sobra não cobre — e a frase diz de quanto é a falta,
 *                    porque "não vai dar" sem o tamanho não deixa decidir
 *                    entre cortar gasto e esticar o prazo.
 * caixa cobre        ela já tem o dinheiro; o prazo vira detalhe.
 */
const ExplicacaoComPrazo: React.FC<{
  meta: GoalProjection;
  contexto: GoalTimeline | null;
  fmt: (v: number) => string;
  onSeparar: () => void;
}> = ({ meta, contexto, fmt, onSeparar }) => {
  const prazo = rotuloDoMes(meta.deadlineMonth!);

  if (meta.reason === 'deadline-passed') {
    return (
      <div className="goal-eta goal-eta-none">
        O prazo era {prazo} e ainda faltam {fmt(meta.remaining)}.
        {meta.cashCoversThis && ' Você tem em caixa para fechar agora.'}
      </div>
    );
  }

  const meses = meta.monthsToDeadline!;
  const emMeses = `${meses} ${meses === 1 ? 'mês' : 'meses'}`;
  const precisa = `Para chegar até ${prazo} são ${fmt(meta.requiredMonthly!)}/mês (${emMeses}).`;

  if (meta.onTrackForDeadline) {
    return (
      <div className="goal-eta goal-eta-no-caminho">
        <p className="goal-eta-caixa-titulo">{precisa} Cabe na sua sobra.</p>
      </div>
    );
  }

  // Não cabe. O que ela pode fazer depende de ter ou não o dinheiro parado.
  const falta = meta.requiredMonthly! - meta.monthlyAllocation;
  /*
   * Quando não sobra NADA, a falta é igual ao necessário — e repetir o número
   * ("são R$ 1.500/mês… faltam R$ 1.500/mês") faz a frase parecer quebrada.
   * Nesse caso o que informa é a sobra em si, não a subtração.
   */
  const desencaixe = meta.monthlyAllocation > 0
    ? `, e desta meta cabem ${fmt(meta.monthlyAllocation)}, então faltam ${fmt(falta)}/mês.`
    : ', então hoje não sobra nada para ela.';
  return (
    <div className={`goal-eta ${meta.cashCoversThis ? 'goal-eta-caixa' : 'goal-eta-none'}`}>
      <p className="goal-eta-caixa-titulo">
        {precisa}{' '}
        {contexto
          ? `Sua sobra projetada é ${fmt(contexto.monthlySurplus)}${desencaixe}`
          : 'Sua sobra projetada não cobre isso.'}
      </p>
      {meta.cashCoversThis && (
        <>
          <button type="button" className="goal-eta-separar" onClick={onSeparar}>
            Separar do caixa agora
          </button>
          <p className="goal-eta-caixa-nota">
            Você já tem {fmt(contexto?.availableCash ?? 0)} em caixa, e o prazo deixa
            de depender da sobra se você separar de uma vez.
          </p>
        </>
      )}
    </div>
  );
};

const ExplicacaoSemRitmo: React.FC<{
  meta: GoalProjection;
  contexto: GoalTimeline | null;
  fmt: (v: number) => string;
  onSeparar: () => void;
}> = ({ meta, contexto, fmt, onSeparar }) => {
  const sobra = contexto
    ? `Sua sobra mensal projetada é de ${fmt(contexto.monthlySurplus)}`
      + ` (mediana de ${contexto.monthsConsidered} ${contexto.monthsConsidered === 1 ? 'mês' : 'meses'}).`
    : 'Sua sobra mensal projetada não é positiva.';

  // Sem o contexto carregado, a frase antiga — ela não estava errada, só era
  // vaga, e vago é melhor que inventado.
  if (!contexto || !meta.cashCoversThis) {
    return (
      <div className="goal-eta goal-eta-none">
        No ritmo atual a meta não avança. {sobra}
      </div>
    );
  }

  return (
    <div className="goal-eta goal-eta-caixa">
      <p className="goal-eta-caixa-titulo">
        Você já tem {fmt(contexto.availableCash)} em caixa
        {contexto.cashCoversAll
          ? ', o bastante para esta meta. Falta separar.'
          : ', o bastante para esta meta, mas não para todas ao mesmo tempo'
            + ` (faltam ${fmt(contexto.totalRemaining)} somando todas).`}
      </p>
      <button type="button" className="goal-eta-separar" onClick={onSeparar}>
        Separar agora
      </button>
      {/* O fluxo negativo continua dito: a reserva acaba, e é ele que decide
          quando. */}
      <p className="goal-eta-caixa-nota">{sobra}</p>
    </div>
  );
};

const Goals: React.FC<GoalsProps> = ({ filteredTransactions, categories, activeMonthLabel, onGoalsChange, onDepositRecorded, refreshTrigger, hideValues }) => {
  const confirmar = useConfirmar();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('reserve');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [depositId, setDepositId] = useState<string | null>(null);
  const [depositValue, setDepositValue] = useState('');
  const [depositBusy, setDepositBusy] = useState(false);
  /** Item 20: projeção de conclusão por meta, vinda da sobra mensal. */
  const [timeline, setTimeline] = useState<Record<string, GoalProjection>>({});
  /*
   * O contexto da projeção, separado das metas.
   *
   * A sobra e o caixa são do CONJUNTO, não de uma meta. Guardá-los aqui é o que
   * permite a frase dizer o número em vez de "não é positiva" — que era vago
   * justamente onde precisava ser concreto.
   */
  const [contexto, setContexto] = useState<GoalTimeline | null>(null);

  /* Delega para `brlOrMasked`: é o que faz o olhinho de "Ocultar valores"
     alcançar esta seção. Antes era um `Intl.NumberFormat` próprio, e como não
     havia um lugar por onde todo dinheiro passasse, o olhinho só chegava nos
     quatro cartões do topo. */
  const fmt = (v: number) => brlOrMasked(v, !!hideValues);

  /*
   * Revalidar não esconde as metas que já estão na tela (15/09): trocar a
   * seção por "Carregando metas..." a cada volta da aba fazia a página pular.
   * O carregando aparece só antes da primeira resposta.
   */
  const [carregadas, setCarregadas] = useState(false);

  /*
   * As metas compartilhadas moram na aba Reservas, como IRMÃS das pessoais.
   *
   * O estado fica aqui e não dentro do componente delas porque esta função
   * precisa da CONTAGEM: com nenhuma reserva pessoal e uma meta compartilhada,
   * o estado vazio abaixo ("Nenhuma reserva criada", com o preview fantasma)
   * seria uma mentira em cima de uma tela que tem meta.
   *
   * `onDepositRecorded` entra porque o aporte é uma despesa de verdade em
   * `transactions`: sem avisar o App, a barra da meta sobe e o saldo do topo
   * fica o de antes — dois números sobre o mesmo dinheiro, discordando.
   */
  const compartilhadas = useMetasCompartilhadas(onDepositRecorded);

  const load = async () => {
    try {
      setLoading(true);
      setGoals(await getGoals());
      setCarregadas(true);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  /*
   * Uma meta pode nascer fora deste card: a proposta de Finn Coach, aceita na
   * conversa. Sem ouvir o aviso, ela tocaria em "Ver a meta" e chegaria numa
   * lista que ainda não tem a meta que acabou de criar.
   */
  useEffect(() => {
    const recarregar = () => { load(); };
    window.addEventListener(EVENTO_METAS_MUDARAM, recarregar);
    return () => window.removeEventListener(EVENTO_METAS_MUDARAM, recarregar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { if (refreshTrigger !== undefined) load(); }, [refreshTrigger]);

  const reserveGoals = goals.filter(g => g.type === 'reserve');
  const categoryGoals = goals.filter(g => g.type === 'category_limit');

  /*
   * Quem só tem limite abre nos limites.
   *
   * A aba padrão é a de reservas. Para quem não tem reserva nenhuma e tem
   * limite, isso é abrir Metas e ver uma tela vazia com a própria meta
   * escondida atrás de um clique — pedido dela, em 06/10.
   *
   * ── UMA vez, e só depois de tudo carregar ────────────────────────────────
   *
   * A aba de reservas também mostra a meta compartilhada e o CONVITE para
   * uma. Essas chegam por outra requisição: decidir só com as metas dela
   * mandaria para os limites alguém com um convite esperando resposta, e o
   * convite ficaria onde ela não olha. Por isso espera as duas.
   *
   * E decide uma vez só. Sem a trava, excluir a última reserva — ou a lista
   * recarregar depois de um aporte — trocaria a aba debaixo do dedo de quem
   * escolheu ficar ali. Depois da primeira decisão, a aba é de quem clica.
   */
  const abaDecidida = useRef(false);
  /** Ela escolheu: daqui em diante a aba não muda sozinha, nem se a carga ainda não terminou. */
  const escolherAba = (t: Tab) => { abaDecidida.current = true; setTab(t); };
  useEffect(() => {
    if (abaDecidida.current || !carregadas || !compartilhadas.carregou) return;
    abaDecidida.current = true;
    setTab(abaInicialDeMetas({
      reservas: reserveGoals.length,
      compartilhadas: compartilhadas.metas.length,
      convites: compartilhadas.convites.length,
      limites: categoryGoals.length,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carregadas, compartilhadas.carregou]);

  const spendingMap = useMemo(() => {
    const map = new Map<string, number>();
    let total = 0;
    filteredTransactions
      .filter(t => t.type === TransactionType.EXPENSE)
      .forEach(t => {
        const key = t.categoryId ?? '__none__';
        // O valor do mês de uma mensal variável, não o cadastrado (15/09).
        map.set(key, (map.get(key) ?? 0) + valorExibido(t));
        total += valorExibido(t);
      });
    map.set('__total__', total);
    return map;
  }, [filteredTransactions]);

  const openEdit = (g: Goal) => {
    setEditingId(g.id);
    setFormData({
      type: g.type,
      name: g.name,
      targetAmount: String(g.targetAmount),
      savedAmount: String(g.savedAmount),
      categoryId: g.categoryId ?? '',
      color: g.color,
      deadlineMonth: g.deadlineMonth ?? '',
    });
    setShowForm(true);
  };

  // O erro morre com o formulário: reabrir mostrando a falha anterior faria
  // parecer que a tentativa nova já nasceu quebrada.
  const closeForm = () => { setShowForm(false); setEditingId(null); setFormError(''); };

  /**
   * O formulário aberto para CRIAR — não só para editar.
   *
   * ── Por que passou a existir (04/09) ─────────────────────────────────────
   *
   * `handleSave` sempre soube criar (`else { await createGoal(payload) }`) e o
   * botão já dizia "Criar Meta" quando não há `editingId`. O que faltava era
   * alguém abrir o formulário sem uma meta existente: o render era
   * `showForm && editingId`, então o ramo de criação era inalcançável.
   *
   * O botão "Preencher manualmente" também já existia, dentro de
   * `EntityQuickEntry` — e era código morto, porque só aparece quando o pai
   * passa `onToggleForm`, e nenhum dos três pais passava.
   *
   * Duas metades prontas, nenhuma ligada à outra. O pedido dela foi ligar.
   *
   * ── Por que isto importa além da conveniência ────────────────────────────
   *
   * Enquanto o campo único era o ÚNICO caminho de criação, o teto de leituras
   * por IA trancava a criação de metas por completo — e a mensagem só podia
   * dizer "volte em 50 minutos". Com o formulário alcançável, o teto vira um
   * desvio em vez de uma parede, igual aos outros campos.
   */
  const abrirParaCriar = () => {
    if (showForm) { closeForm(); return; }
    setEditingId(null);
    setFormData({ ...EMPTY_FORM });
    setFormError('');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.targetAmount) return;
    setFormError('');
    setSaving(true);
    try {
      const payload = {
        type: formData.type,
        name: formData.name.trim(),
        targetAmount: parseFloat(formData.targetAmount),
        savedAmount: parseFloat(formData.savedAmount || '0'),
        categoryId: formData.categoryId || null,
        color: formData.color,
        // String vazia não é "sem prazo" para o zod — o schema aceita `null`
        // ou 'AAAA-MM', e '' reprovaria no regex.
        deadlineMonth: formData.deadlineMonth || null,
      };
      if (editingId) {
        await updateGoal(editingId, payload);
      } else {
        await createGoal(payload);
      }
      await load();
      closeForm();
      onGoalsChange?.();
    } catch (e) {
      /*
       * Antes isto era só `console.error`. Salvar falhava, o botão voltava de
       * "Salvando…" para "Criar Meta", e mais nada acontecia — nem mensagem,
       * nem formulário fechando. Da cadeira dela, o clique não fez efeito
       * nenhum, então o gesto seguinte é clicar de novo.
       */
      console.error(e);
      setFormError(mensagemDeErro(e, 'Não consegui salvar esta meta.'));
    } finally {
      setSaving(false);
    }
  };

  /*
   * O voltar do celular fecha ESTE formulário antes de perguntar se quer sair.
   *
   * O relato foi exato: "quando estou com um formulário pra preencher, com o
   * teclado em evidência, e clico em voltar, queria que o form fechasse".
   * Só os modais e os menus estavam na pilha — os formulários que vivem DENTRO
   * da seção não, então voltar deles caía direto na pergunta de sair.
   */
  useFechavelComVoltar(showForm, closeForm);

  /**
   * Excluir a meta — e perguntar sobre os aportes, quando existirem.
   *
   * Relato: "apaguei a meta mas o aporte ainda tá em transações, acho q devia
   * perguntar se eu queria deletar as transações de aporte até agora".
   *
   * A pergunta não é de arrumação. O aporte REDUZ o saldo em caixa — o
   * dinheiro saiu da conta corrente — e não reduz o patrimônio, porque
   * continua sendo dela na reserva. Então apagar os aportes devolve aquele
   * dinheiro ao saldo em caixa, e não apagar deixa o saldo como está.
   *
   * É por isso que a nota diz o efeito em vez de dizer "remove os
   * lançamentos": a escolha só é possível se ela souber em que número mexe.
   *
   * Desmarcada por padrão. Manter é o que se corrige depois — apagar não.
   */
  /*
   * Entrar no Finn Coach é aceitar ser cobrada, e por isso pergunta antes.
   *
   * A regra dela (05/10): a pessoa "tem que tá ciente" de que o Finn "vai
   * encher o saco". Um interruptor que liga cobrança sem dizer o que vem é
   * consentimento de mentira — então a caixa diz o que ele faz, com que
   * frequência, e que sair é aqui mesmo.
   *
   * Sair NÃO pergunta: é um toque, como combinado. Fricção na saída é o que
   * transformaria um coach contratado num que não larga.
   */
  const [erroDoCoach, setErroDoCoach] = useState('');
  const alternarCoach = async (g: Goal) => {
    const entrando = !g.finnCoach;
    if (entrando && !await confirmar({
      // O rosto de quem vai cobrar: o combinado é com ele.
      ilustracao: <FinnAvatar tamanho={64} />,
      titulo: 'Colocar esta meta no Finn Coach?',
      alvo: g.name,
      texto: 'O Finn passa a te cobrar: o placar na segunda e na quinta, e um aviso se '
        + 'você ficar dois dias sem lançar. Ele é direto e não elogia à toa. '
        + 'O recado fica sempre na bolha do Finn, e chega também por um aviso só: pelo WhatsApp, se você '
        + 'usa o FinTracking lá; senão, pela notificação do app. Você sai quando quiser, neste mesmo card.',
      acao: 'Pode me cobrar',
    })) return;
    try {
      setErroDoCoach('');
      await ligarCoach(g.id, entrando);
      await load();
    } catch (e) {
      setErroDoCoach(mensagemDeErro(e, 'Não consegui mudar o coach desta meta.'));
    }
  };

  const handleDelete = async (id: string) => {
    const meta = goals.find(g => g.id === id);
    const aportes = meta?.aportes ?? { n: 0, total: 0 };
    /*
     * Variável local, não estado.
     *
     * A caixa é dona do que desenha e devolve o valor por `aoResponder` — com
     * estado aqui, o `pedido` guardado pelo provedor congelaria no valor
     * inicial e a leitura depois do `await` viria de um closure velho.
     */
    let apagarAportes = false;

    const ok = await confirmar({
      titulo: 'Excluir esta meta?',
      texto: aportes.n > 0
        ? 'O progresso guardado nela some junto.'
        : 'O progresso guardado nela some junto. Os lançamentos continuam onde estão.',
      acao: 'Excluir',
      perigo: true,
      caixaDeSelecao: aportes.n > 0 ? {
        rotulo: `Apagar também ${aportes.n === 1 ? 'o aporte' : `os ${aportes.n} aportes`} `
          + `(${fmt(aportes.total)}) que você lançou nela`,
        nota: `Sem marcar, eles continuam nas transações e o saldo em caixa segue como está. `
          + `Marcando, ${fmt(aportes.total)} voltam para o seu saldo.`,
        aoResponder: v => { apagarAportes = v; },
      } : undefined,
    });
    if (!ok) return;

    const { apagados } = await deleteGoal(id, { apagarAportes });
    await load();
    onGoalsChange?.();
    // O aporte apagado mexe no saldo e nas transações; sem avisar, as outras
    // telas continuariam mostrando o número de antes.
    if (apagados > 0) onDepositRecorded?.();
  };

  /**
   * Item 27 — aportar em uma meta agora REGISTRA a movimentação.
   *
   * Antes isto só incrementava `savedAmount`. A reserva crescia sem deixar
   * rastro nenhum, enquanto o mesmo aporte feito pelo formulário de transação
   * (que grava debitDirection='debit') deixava. Dois caminhos para a mesma
   * ação, um invisível.
   *
   * Nota de modelagem: um débito de fonte NÃO reduz o saldo projetado, e isso
   * é de propósito — o dinheiro na reserva continua sendo seu, o saldo é
   * patrimônio, não caixa disponível. O que muda aqui é existir histórico:
   * sem ele não há como responder "quando chego na meta?" (item 20) a partir
   * do ritmo real de aportes.
   */
  const handleDeposit = async (goal: Goal) => {
    const val = numero(depositValue);
    if (isNaN(val) || val <= 0) return;
    setDepositBusy(true);
    try {
      const hoje = new Date();
      const iso = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-${String(hoje.getDate()).padStart(2, '0')}`;
      await createTransaction({
        type: TransactionType.EXPENSE,
        paymentType: PaymentType.VARIABLE,
        description: `Aporte: ${goal.name}`,
        value: val,
        hasInstallments: false,
        isMonthly: false,
        paymentMethod: null,
        debitDirection: 'debit',
        debitSourceType: 'goal',
        debitSourceId: goal.id,
        date: iso,
        categoryId: null as any,
      } as any);
      await updateGoal(goal.id, {
        type: goal.type, name: goal.name, targetAmount: goal.targetAmount,
        savedAmount: goal.savedAmount + val, categoryId: goal.categoryId ?? null, color: goal.color,
      });
      await load();
      setDepositId(null);
      setDepositValue('');
      onGoalsChange?.();
      onDepositRecorded?.();
    } finally {
      setDepositBusy(false);
    }
  };

  // Item 20 — recarrega a projeção de conclusão sempre que as metas mudarem.
  useEffect(() => {
    let vivo = true;
    getGoalTimeline(24)
      .then(t => {
        if (!vivo) return;
        setTimeline(Object.fromEntries(t.goals.map(g => [g.goalId, g])));
        setContexto(t);
      })
      .catch(() => { /* projeção é complemento; a lista de metas funciona sem ela */ });
    return () => { vivo = false; };
  }, [goals, refreshTrigger]);


  const handleCategoryChange = (catId: string) => {
    const cat = categories.find(c => c.id === catId);
    setFormData(f => ({
      ...f,
      categoryId: catId,
      name: f.name || (cat?.name ?? ''),
      color: f.color === COLORS[0] && cat?.color ? cat.color : f.color,
    }));
  };

  const getSpending = (g: Goal) =>
    g.categoryId ? (spendingMap.get(g.categoryId) ?? 0) : (spendingMap.get('__total__') ?? 0);

  const statusColor = (pct: number) =>
    pct >= 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : undefined;

  /**
   * O prazo escolhido é exatamente o de um atalho?
   *
   * Serve só para o seletor não repetir o que o chip já diz. Comparar o VALOR
   * e não guardar "qual chip foi clicado" é o que faz a marcação sobreviver a
   * reabrir o formulário: editando um teto que vale por 6 meses, o chip acende
   * sem ninguém ter clicado nele.
   */
  const cobertoPorAtalho = !!formData.deadlineMonth
    && formData.type === 'category_limit'
    && ATALHOS_DE_PRAZO.some(a =>
      a.meses !== null && somarMeses(mesCorrente(), a.meses) === formData.deadlineMonth);

  return (
    <div className="goals-container">
      {/* Sem botão "+ Nova Meta": criar é sempre pelo campo único abaixo.
          O formulário existe só para editar uma meta que já existe. */}
      <div className="goals-header">
        <h2>Metas Financeiras</h2>
      </div>

      {/* Campo único — a opção principal, então vem primeiro. */}
      {!editingId && (
        <EntityQuickEntry
          modo="goal"
          categories={categories}
          onCreate={async (d) => { await createGoal(d); await load(); onGoalsChange?.(); }}
          onCategoryCreated={onGoalsChange}
          /* Ligam o botão "Preencher manualmente" e o desvio do teto de IA.
             Sem eles, o botão não é renderizado — ver `abrirParaCriar`. */
          onToggleForm={abrirParaCriar}
          formAberto={showForm && !editingId}
        />
      )}

      {/* Abas abaixo do campo: elas filtram a LISTA, não o que se escreve —
          o campo único decide sozinho se é reserva ou teto de gasto. Acima do
          campo elas pareciam escolher o tipo do que ia ser criado. */}
      <div className="goals-tabs">
        <button className={`goals-tab${tab === 'reserve' ? ' active' : ''}`} onClick={() => escolherAba('reserve')}>
          Reservas
          {reserveGoals.length > 0 && <span className="goals-tab-badge">{reserveGoals.length}</span>}
        </button>
        <button className={`goals-tab${tab === 'category_limit' ? ' active' : ''}`} onClick={() => escolherAba('category_limit')}>
          Limites de Gastos
          {categoryGoals.length > 0 && <span className="goals-tab-badge">{categoryGoals.length}</span>}
        </button>
      </div>

      {/*
        O formulário serve para EDITAR e para CRIAR.

        Era `showForm && editingId`, e o `editingId` tornava o ramo de criação
        inalcançável — mesmo com `handleSave` sabendo criar e o botão já dizendo
        "Criar Meta". Ver a nota em `abrirParaCriar`.
      */}
      {showForm && (
        <div className="goals-form">
          {/* O título passou a ser necessário: antes só havia um estado
              possível, agora são dois e eles gravam coisas diferentes. */}
          <h3 className="goals-form-titulo">{editingId ? 'Editar meta' : 'Nova meta'}</h3>
          <div className="goals-form-row">
            <div className="goals-form-group">
              <label>Tipo</label>
              <div className="goals-type-toggle">
                <button className={formData.type === 'reserve' ? 'active' : ''} onClick={() => setFormData(f => ({ ...f, type: 'reserve' }))}>
                  Reserva
                </button>
                <button className={formData.type === 'category_limit' ? 'active' : ''} onClick={() => setFormData(f => ({ ...f, type: 'category_limit' }))}>
                  Limite de Gasto
                </button>
              </div>
            </div>

            {formData.type === 'reserve' && (
              <div className="goals-form-group" style={{ flex: 2 }}>
                <label>Nome</label>
                <input
                  type="text"
                  maxLength={LIMITES.nome}
                  placeholder="Ex: Fundo de emergência"
                  value={formData.name}
                  onChange={e => setFormData(f => ({ ...f, name: e.target.value }))}
                />
              </div>
            )}
          </div>

          {formData.type === 'category_limit' && (
            <div className="goals-form-row">
              <div className="goals-form-group">
                <label>Categoria</label>
                <select value={formData.categoryId} onChange={e => handleCategoryChange(e.target.value)}>
                  <option value="">Todos os gastos</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="goals-form-group" style={{ flex: 2 }}>
                <label>Nome</label>
                <input
                  type="text"
                  maxLength={LIMITES.nome}
                  placeholder="Ex: Limite de alimentação"
                  value={formData.name}
                  onChange={e => setFormData(f => ({ ...f, name: e.target.value }))}
                />
              </div>
            </div>
          )}

          {/*
            Duas COLUNAS, não quatro campos numa fila.

            Numa fila, o grupo do prazo é o mais alto — cinco atalhos quebram
            em três fileiras — e a Cor era empurrada para a linha de baixo,
            deixando um vazio à esquerda do bloco alto. Era o relato: "no
            desktop deixar o limite mensal e cor na mesma linha do vale até".

            O cartão tem 566px no grid de duas colunas do painel, então três
            grupos lado a lado não cabem: só os atalhos pedem ~500px. O que
            cabe é o que ela descreveu — valor e cor EMPILHADOS ao lado do
            prazo, duas colunas de ~230 que se equilibram em altura (52+53
            contra 101).
          */}
          <div className="goals-form-row goals-form-row-prazo">
            <div className="goals-form-coluna">
              <div className="goals-form-group">
                <label>{formData.type === 'reserve' ? 'Valor da meta' : 'Limite mensal'}</label>
                <input
                  type="number"
                  placeholder="0,00"
                  value={formData.targetAmount}
                  onChange={e => setFormData(f => ({ ...f, targetAmount: e.target.value }))}
                />
              </div>
              {formData.type === 'reserve' && (
                <div className="goals-form-group">
                  <label>Já guardado</label>
                  <input
                    type="number"
                    placeholder="0,00"
                    value={formData.savedAmount}
                    onChange={e => setFormData(f => ({ ...f, savedAmount: e.target.value }))}
                  />
                </div>
              )}
              <div className="goals-form-group goals-form-color">
                <label>Cor</label>
                <div className="goals-color-picker">
                  {COLORS.map(c => (
                    <button
                      key={c}
                      className={`goals-color-dot${formData.color === c ? ' selected' : ''}`}
                      style={{ background: c }}
                      onClick={() => setFormData(f => ({ ...f, color: c }))}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/*
              O prazo é OPCIONAL, e o rótulo diz isso.

              Sem a palavra, um campo vazio num formulário parece pendência, e
              ela preencheria uma data qualquer só para o formulário parar de
              parecer incompleto — que é o jeito mais fácil de encher o app de
              prazo que ninguém escolheu.

              Atalhos e seletor na MESMA fileira: são duas maneiras de responder
              UMA pergunta, e empilhados pareciam duas perguntas.

              Os atalhos só existem no TETO. Um teto se pensa em duração — "só
              este mês", "os próximos 6" — e foi assim que ela pediu. Uma
              reserva se pensa em data: a viagem é em dezembro, não "daqui a 4
              meses". Atalho de duração ali empurraria para o jeito errado de
              pensar a coisa.
            */}
            <div className="goals-form-group goals-form-prazo">
              <label>
                {formData.type === 'reserve' ? 'Prazo (opcional)' : 'Vale até (opcional)'}
              </label>
              <div className="goals-prazo-linha">
                {formData.type === 'category_limit' && ATALHOS_DE_PRAZO.map(a => {
                  const valor = a.meses === null ? '' : somarMeses(mesCorrente(), a.meses);
                  return (
                    <button
                      key={a.rotulo}
                      type="button"
                      className={`goals-prazo-chip${formData.deadlineMonth === valor ? ' ativo' : ''}`}
                      onClick={() => setFormData(f => ({ ...f, deadlineMonth: valor }))}
                    >
                      {a.rotulo}
                    </button>
                  );
                })}
                {/*
                  O seletor não repete o que um chip já está dizendo.

                  Passando o valor sempre, "6 meses" e "jan/2027" ficavam os
                  dois destacados na mesma fileira — dois controles marcados
                  para uma escolha só, e nenhum dos dois explicando por quê.
                */}
                <SeletorDeMes
                  value={cobertoPorAtalho ? '' : formData.deadlineMonth}
                  onChange={v => setFormData(f => ({ ...f, deadlineMonth: v }))}
                  vazio={formData.type === 'category_limit' ? 'Outro mês' : 'Escolher mês'}
                  minimo={mesCorrente()}
                />
              </div>
            </div>
          </div>

          {formError && <p className="goals-form-error">{formError}</p>}
          <div className="goals-form-actions">
            <button className="goals-btn-cancel" onClick={closeForm}>Cancelar</button>
            <button className="goals-btn-save" onClick={handleSave} disabled={saving} aria-busy={saving}>
              {editingId ? 'Atualizar' : 'Criar Meta'}
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      {loading && !carregadas ? (
        <div className="loading-wrap">
          <div className="loading-ring" />
          <span className="loading-text">Carregando metas...</span>
        </div>
      ) : tab === 'reserve' ? (
        /*
         * As compartilhadas entram na conta do vazio.
         *
         * Sem isto, quem tem uma vaquinha e nenhuma reserva própria veria
         * "Nenhuma reserva criada" com o preview fantasma — em cima de uma
         * tela que tem meta. O convite recebido aparece ANTES de tudo, porque
         * responder a ele é a ação mais urgente que esta aba oferece.
         */
        reserveGoals.length === 0 && compartilhadas.metas.length === 0 ? (
          <>
          <ConvitesDeMeta
            convites={compartilhadas.convites}
            onResponder={compartilhadas.responder}
          />
          <div className="empty-preview-wrap">
            <span className="empty-preview-badge">Exemplo</span>
            <div className="goals-grid empty-preview-ghost">
              {[
                { name: 'Fundo de Emergência', target: 10000, saved: 2500, color: '#7c3aed' },
                { name: 'Viagem Europa',        target: 15000, saved: 4800, color: '#3b82f6' },
              ].map((g, i) => {
                const pct = (g.saved / g.target) * 100;
                return (
                  <div key={i} className="goal-card" style={{ '--accent': g.color } as React.CSSProperties}>
                    <div className="goal-card-top" style={{ borderTopColor: g.color }}>
                      <div className="goal-card-header">
                        <span className="goal-card-name">{g.name}</span>
                        <div className="goal-card-actions">
                          <button className="goal-action-btn">✎</button>
                          <button className="goal-action-btn delete">✕</button>
                        </div>
                      </div>
                      <div className="goal-amounts">
                        <span className="goal-saved" style={{ color: g.color }}>{fmt(g.saved)}</span>
                        <span className="goal-sep">/</span>
                        <span className="goal-target">{fmt(g.target)}</span>
                      </div>
                      <div className="goal-bar-wrap">
                        <div className="goal-bar-fill" style={{ width: `${pct}%`, background: g.color }} />
                      </div>
                      <div className="goal-bar-labels">
                        <span className="goal-pct" style={{ color: g.color }}>{formatPct(pct)}</span>
                        <span className="goal-remaining">Faltam {fmt(g.target - g.saved)}</span>
                      </div>
                    </div>
                    <button className="goal-deposit-btn" style={{ color: g.color, borderColor: g.color + '55' }}>
                      + Registrar depósito
                    </button>
                  </div>
                );
              })}
            </div>
            <div className="empty-cta">
              <p className="empty-cta-title">Nenhuma reserva criada</p>
              <p className="empty-cta-sub">Crie metas de economia para acompanhar seu progresso rumo a objetivos financeiros.</p>
            </div>
          </div>
          <CriarMetaCompartilhada
            onCriar={compartilhadas.criar}
            exemplo={compartilhadas.metas.length === 0 ? <ExemploDeMetaCompartilhada fmt={fmt} /> : undefined}
          />
          </>
        ) : (
          <>
          <ConvitesDeMeta
            convites={compartilhadas.convites}
            onResponder={compartilhadas.responder}
          />
          <div className="goals-grid">
            {reserveGoals.map(g => {
              const pct = g.targetAmount > 0 ? Math.min((g.savedAmount / g.targetAmount) * 100, 100) : 0;
              const remaining = g.targetAmount - g.savedAmount;
              const done = remaining <= 0;
              return (
                <div className="goal-card" key={g.id} style={{ '--accent': g.color } as React.CSSProperties}>
                  <div className="goal-card-top" style={{ borderTopColor: g.color }}>
                    <div className="goal-card-header">
                      <span className="goal-card-name">{g.name}</span>
                      <div className="goal-card-actions">
                        <button className="goal-action-btn" onClick={() => openEdit(g)}>✎</button>
                        <button className="goal-action-btn delete" onClick={() => handleDelete(g.id)}>✕</button>
                      </div>
                    </div>

                    <div className="goal-amounts">
                      <span className="goal-saved" style={{ color: done ? '#10b981' : g.color }}>{fmt(g.savedAmount)}</span>
                      <span className="goal-sep">/</span>
                      <span className="goal-target">{fmt(g.targetAmount)}</span>
                    </div>

                    <div className="goal-bar-wrap">
                      <div className="goal-bar-fill" style={{ width: `${pct}%`, background: done ? '#10b981' : g.color }} />
                    </div>

                    <div className="goal-bar-labels">
                      <span className="goal-pct" style={{ color: done ? '#10b981' : g.color }}>{formatPct(pct)}</span>
                      {done
                        ? <span className="goal-done-label">✓ Meta atingida!</span>
                        : <span className="goal-remaining">Faltam {fmt(remaining)}</span>
                      }
                    </div>

                    {/* Item 20 — quando chego na meta? Derivado da sobra mensal
                        projetada, não de um ritmo de aportes que não existia. */}
                    {/*
                      A ORDEM importa: prazo primeiro.
                      
                      Antes o primeiro ramo era "tem data projetada?", e uma
                      meta com prazo que está no caminho tem data projetada —
                      ela caía ali e o prazo nunca aparecia, justamente no caso
                      em que a notícia é boa.
                      
                      Prazo definido é uma escolha DELA; a data projetada é um
                      cálculo nosso. A escolha dela manda.
                    */}
                    {!done && timeline[g.id] && (
                      timeline[g.id].deadlineMonth ? (
                        <ExplicacaoComPrazo
                          meta={timeline[g.id]}
                          contexto={contexto}
                          fmt={fmt}
                          onSeparar={() => setDepositId(g.id)}
                        />
                      ) : timeline[g.id].projectedCompletionMonth ? (
                        <div className="goal-eta">
                          <span className="goal-eta-label">No ritmo atual, você chega em</span>
                          <strong style={{ color: g.color }}>
                            {rotuloDoMes(timeline[g.id].projectedCompletionMonth!)}
                          </strong>
                          <span className="goal-eta-rate">
                            ({fmt(timeline[g.id].monthlyAllocation)}/mês
                            {!timeline[g.id].reason && Object.keys(timeline).length > 1 ? ', rateado entre suas metas' : ''})
                          </span>
                        </div>
                      ) : (
                        <ExplicacaoSemRitmo
                          meta={timeline[g.id]}
                          contexto={contexto}
                          fmt={fmt}
                          onSeparar={() => setDepositId(g.id)}
                        />
                      )
                    )}
                  </div>

                  {depositId === g.id ? (
                    <div className="goal-deposit-row">
                      {/* A máscara dos campos de valor (05/10) — ver a nota no
                          "Guardar dinheiro" de `MetaCompartilhadaCard`. */}
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="Valor a adicionar"
                        value={depositValue}
                        onChange={e => setDepositValue(mascara(e.target.value))}
                        autoFocus
                        disabled={depositBusy}
                        onKeyDown={e => e.key === 'Enter' && !depositBusy && handleDeposit(g)}
                      />
                      <button
                        className="goal-deposit-save"
                        style={{ background: g.color }}
                        disabled={depositBusy}
                        aria-busy={depositBusy}
                        onClick={() => handleDeposit(g)}
                      >
                        +
                      </button>
                      <button className="goal-deposit-cancel" disabled={depositBusy} onClick={() => setDepositId(null)}>✕</button>
                    </div>
                  ) : (
                    <button
                      className="goal-deposit-btn"
                      style={{ color: g.color, borderColor: g.color + '55' }}
                      onClick={() => { setDepositId(g.id); setDepositValue(''); }}
                    >
                      + Registrar depósito
                    </button>
                  )}
                </div>
              );
            })}

            {/*
              * Os cards compartilhados entram na MESMA grade das reservas
              * pessoais, e não numa lista abaixo.
              *
              * É a decisão do desenho: "compartilhada" não é um TIPO de meta,
              * é quem participa. Numa grade própria ela viraria uma terceira
              * categoria visual, e a pessoa passaria a ter dois lugares para
              * procurar "minhas metas" — que foi exatamente o motivo de não
              * existir uma terceira aba.
              *
              * Depois das pessoais porque a ordem da grade é estável: as
              * pessoais já estavam lá, e uma vaquinha nova não deve empurrar a
              * reserva de emergência para baixo.
              */}
            {compartilhadas.metas.map(m => (
              <MetaCompartilhadaCard
                key={m.id}
                meta={m}
                acoes={compartilhadas.acoes}
                fmt={fmt}
              />
            ))}
          </div>
          <CriarMetaCompartilhada
            onCriar={compartilhadas.criar}
            exemplo={compartilhadas.metas.length === 0 ? <ExemploDeMetaCompartilhada fmt={fmt} /> : undefined}
          />
          </>
        )
      ) : (
        categoryGoals.length === 0 ? (
          <div className="empty-preview-wrap">
            <span className="empty-preview-badge">Exemplo</span>
            <div className="goals-grid empty-preview-ghost">
              {[
                { name: 'Limite de Alimentação', target: 800, spent: 450, color: '#f59e0b' },
                { name: 'Lazer e Entretenimento', target: 300, spent: 270, color: '#ef4444' },
              ].map((g, i) => {
                const pct = (g.spent / g.target) * 100;
                const barColor = pct >= 80 ? (pct >= 100 ? '#ef4444' : '#f59e0b') : g.color;
                return (
                  <div key={i} className="goal-card" style={{ '--accent': g.color } as React.CSSProperties}>
                    <div className={`goal-card-top${pct >= 80 ? ' near-limit' : ''}`} style={{ borderTopColor: barColor }}>
                      <div className="goal-card-header">
                        <div className="goal-name-row">
                          <span className="goal-card-name">{g.name}</span>
                          {pct >= 80 && <span className="goal-status-pill warn">Atenção</span>}
                        </div>
                        <div className="goal-card-actions">
                          <button className="goal-action-btn">✎</button>
                          <button className="goal-action-btn delete">✕</button>
                        </div>
                      </div>
                      <div className="goal-month-tag">{activeMonthLabel}</div>
                      <div className="goal-amounts">
                        <span className="goal-saved" style={{ color: barColor }}>{fmt(g.spent)}</span>
                        <span className="goal-sep">/</span>
                        <span className="goal-target">{fmt(g.target)}</span>
                      </div>
                      <div className="goal-bar-wrap">
                        <div className="goal-bar-fill" style={{ width: `${Math.min(pct, 100)}%`, background: barColor }} />
                      </div>
                      <div className="goal-bar-labels">
                        <span className="goal-pct" style={{ color: barColor }}>{formatPct(pct)}</span>
                        <span className="goal-remaining">Disponível: {fmt(g.target - g.spent)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="empty-cta">
              <p className="empty-cta-title">Nenhum limite cadastrado</p>
              <p className="empty-cta-sub">Defina limites de gastos por categoria para controlar melhor suas despesas mensais.</p>
            </div>
          </div>
        ) : (
          <>
          {erroDoCoach && <p className="goals-form-error" role="alert">{erroDoCoach}</p>}
          <div className="goals-grid">
            {categoryGoals.map(g => {
              const spent = getSpending(g);
              const pct = g.targetAmount > 0 ? (spent / g.targetAmount) * 100 : 0;
              /*
               * Teto vencido não acusa nada.
               *
               * O servidor já parou de alertar nele (`limiteVigente.ts`). Se o
               * cartão continuasse pintando "Excedido" em âmbar, a tela estaria
               * contradizendo o alerta — e das duas versões a errada é sempre a
               * que ela está olhando.
               *
               * Os números ficam: é histórico, e histórico não é alarme.
               */
              const vencido = limiteVencido(g);
              const barColor = (vencido ? null : statusColor(pct)) ?? g.color;
              const remaining = g.targetAmount - spent;
              const cat = g.categoryId ? categories.find(c => c.id === g.categoryId) : null;
              return (
                <div className="goal-card" key={g.id} style={{ '--accent': g.color } as React.CSSProperties}>
                  <div className={`goal-card-top${vencido ? ' vencido' : pct >= 100 ? ' over-limit' : pct >= 80 ? ' near-limit' : ''}`} style={{ borderTopColor: barColor }}>
                    <div className="goal-card-header">
                      <div className="goal-name-row">
                        {cat && <span className="goal-cat-dot" style={{ background: cat.color }} />}
                        <span className="goal-card-name">{g.name}</span>
                        {!vencido && pct >= 100 && <span className="goal-status-pill over">Excedido</span>}
                        {!vencido && pct >= 80 && pct < 100 && <span className="goal-status-pill warn">Atenção</span>}
                      </div>
                      <div className="goal-card-actions">
                        <button className="goal-action-btn" onClick={() => openEdit(g)}>✎</button>
                        <button className="goal-action-btn delete" onClick={() => handleDelete(g.id)}>✕</button>
                      </div>
                    </div>

                    {/*
                      Mês e vigência na MESMA linha.
                      
                      O selo estava numa linha própria embaixo, e empurrava os
                      valores para baixo — um cartão de teto ficou três linhas
                      mais alto para dizer uma coisa de quatro palavras. Os
                      dois respondem "quando", então moram juntos.
                      
                      O vencido continua na lista, marcado — apagá-lo sozinho
                      faria um teto sumir sem ninguém ter apagado, e ela
                      procuraria o que aconteceu. O que o vencido perde é o
                      poder: não alerta mais, e a IA não o cita
                      (`services/limiteVigente.ts`).
                    */}
                    <div className="goal-quando">
                      <span className="goal-month-tag">{activeMonthLabel}</span>
                      {/*
                        O selo do coach mora AQUI, com as outras etiquetas, e não
                        ao lado do nome. Lá ele disputava a linha com o título,
                        que é quem encolhe — no celular "Limite de Transporte"
                        virava "Lim…" para caber um selo. O nome da meta não
                        perde espaço para etiqueta nenhuma.
                      */}
                      {g.finnCoach && <span className="goal-selo-coach">Finn Coach</span>}
                      {g.deadlineMonth && (
                        <span className={`goal-vigencia${limiteVencido(g) ? ' vencida' : ''}`}>
                          {limiteVencido(g)
                            ? `valeu até ${rotuloDoMes(g.deadlineMonth)}`
                            : `vale até ${rotuloDoMes(g.deadlineMonth)}`}
                        </span>
                      )}
                    </div>

                    <div className="goal-amounts">
                      <span className="goal-saved" style={{ color: barColor }}>{fmt(spent)}</span>
                      <span className="goal-sep">/</span>
                      <span className="goal-target">{fmt(g.targetAmount)}</span>
                    </div>

                    <div className="goal-bar-wrap">
                      <div className="goal-bar-fill" style={{ width: `${Math.min(pct, 100)}%`, background: barColor }} />
                    </div>

                    <div className="goal-bar-labels">
                      <span className="goal-pct" style={{ color: barColor }}>{formatPct(pct)}</span>
                      {remaining > 0
                        ? <span className="goal-remaining">Disponível: {fmt(remaining)}</span>
                        : <span className="goal-over-label">+{fmt(Math.abs(remaining))} acima do limite</span>
                      }
                    </div>

                    {/*
                      O Finn Coach. A frase vem pronta do servidor — a mesma do
                      push e da bolha — e aqui só se exibe.

                      Só limite de CATEGORIA e vigente oferece o coach: é o que
                      ele sabe acompanhar. Num teto vencido ou sem categoria, o
                      botão prometeria uma cobrança que nunca chega.
                    */}
                    {g.finnCoach && g.coachTexto && (
                      <p className="goal-coach-fala">{g.coachTexto}</p>
                    )}
                    {!vencido && g.categoryId && (
                      <button
                        type="button"
                        className={`goal-coach-btn${g.finnCoach ? ' ligado' : ''}`}
                        onClick={() => alternarCoach(g)}
                      >
                        {g.finnCoach ? 'Sair do coach' : 'Quero que o Finn me cobre'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          </>
        )
      )}
    </div>
  );
};

export default Goals;
